import React from 'react'
import styled from 'styled-components'

import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'

// ── Design tokens ─────────────────────────────────────────────────────────────

export const PAGE_BG = '#FCF9F9'
export const CARD_BORDER = '#E1E3E5'
export const TITLE_COLOR = '#28364F'
export const AGENT_META_COLOR = '#666'
export const SECTION_LABEL_COLOR = '#717680'

// ── Page layout ───────────────────────────────────────────────────────────────

// Wraps the sticky sub-header + page content so the sticky bar has a
// deterministic DOM parent that spans the full content height — without
// this, the bar's sticky containing block is the overflow:auto layout
// scroll container and the bar can unstick partway through long pages.
export const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 32px 40px 60px;
  background: ${PAGE_BG};
  box-sizing: border-box;
`

export const ContentWrapper = styled.div`
  max-width: 860px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

// ── Insight header ────────────────────────────────────────────────────────────

export const InsightHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const DetectionBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const DetectionLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const DetectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${TITLE_COLOR};
`

export const InsightTitle = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: ${TITLE_COLOR};
  line-height: 44px;
  margin: 0;
`

export const InsightDescription = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: #6c737d;
  line-height: 1.6;
  margin: 0;
`

export const AgentMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 400;
  color: ${AGENT_META_COLOR};
`

export const AgentMetaName = styled.span`
  font-weight: 500;
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

export const UpdatedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: var(--Font-size-text-xs, 12px);
  font-weight: 500;
  line-height: var(--Line-height-text-xs, 16px);
  letter-spacing: 0;
  color: #375d93;
  background: #e5eaf1;
  border: 1px solid #b9c7db;
  border-radius: 6px;
`

// ── Section cards ─────────────────────────────────────────────────────────────

export const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid ${CARD_BORDER};
  border-radius: 20px;
  padding: 24px 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/** Header row inside a card: title on the left, action buttons on the right. */
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

/** Bold section title used inside cards (replaces external SectionLabel/SectionTitle). */
export const CardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${TITLE_COLOR};
`

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

/** Icon-only action button — wraps a single MUI icon (fontSize 14). */
export const CardIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`

/** Text + icon action button — e.g. "Open in charts ↗". */
export const CardTextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f3f4f6;
  }
`

// ── Two-column glance layout ──────────────────────────────────────────────────

export const GlanceColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
`

export const GlancePanel = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const GlancePanelDivider = styled.div`
  width: 1px;
  background: ${CARD_BORDER};
  align-self: stretch;
  flex-shrink: 0;
  margin: 0 20px;

  @media (max-width: 600px) {
    width: 100%;
    height: 1px;
    margin: 16px 0;
  }
`

// ── Helpers ───────────────────────────────────────────────────────────────────

const METRIC_SPLIT_RE = /([+-]?\d[\d,]*\.?\d*[%x×]?)/g
const METRIC_TEST_RE = /^[+-]?\d[\d,]*\.?\d*[%x×]?$/

/** Wraps numeric metrics in <strong> so they render bold inside TakeawayItem. */
export function renderWithBoldMetrics(text: string): React.ReactNode {
  const parts = text.split(METRIC_SPLIT_RE)
  return parts.map((part, i) =>
    METRIC_TEST_RE.test(part)
      ? React.createElement('strong', { key: i }, part)
      : part,
  )
}

// ── Takeaways ─────────────────────────────────────────────────────────────────

export const TakeawayList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const TakeawayItem = styled.li`
  font-size: 14px;
  color: #6c737d;
  line-height: 1.55;

  strong {
    color: ${TITLE_COLOR};
    font-weight: 700;
  }
`

// ── Verbatims ─────────────────────────────────────────────────────────────────

export const VerbatimList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const VerbatimItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 14px;
  border-left: 2px solid ${CARD_BORDER};
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-left-color: #9ca3af;
  }

  &:focus-visible {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

export const VerbatimQuote = styled.p`
  font-size: 14px;
  color: ${TITLE_COLOR};
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const VerbatimMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const StarRow = styled.span`
  display: inline-flex;
  gap: 1px;
`

export const Star = styled.span<{ $filled: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$filled ? '#FBBF24' : '#D1D5DB')};
  line-height: 1;
`

export const VerbatimMetaDot = styled.span`
  font-size: 11px;
  color: ${SECTION_LABEL_COLOR};
`

export const VerbatimSource = styled.span`
  font-size: 11.5px;
  color: ${SECTION_LABEL_COLOR};
`

/** Single-line "Brand · Product" attribution below the meta row. */
export const VerbatimProductLine = styled.span`
  font-size: 12px;
  color: ${AGENT_META_COLOR};
  font-weight: 500;

  .brand {
    color: ${SECTION_LABEL_COLOR};
    font-weight: 400;
  }
`

// ── Dropdown menu ─────────────────────────────────────────────────────────────

export const DropdownMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid ${CARD_BORDER};
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  display: flex;
  flex-direction: column;
`

export const DropdownGroupHeading = styled.span`
  display: block;
  padding: 6px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${SECTION_LABEL_COLOR};
`

export const DropdownItem = styled.button<{ $selected?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 16px;
  font-size: 14px;
  font-weight: ${(p) => (p.$selected ? '600' : '400')};
  color: ${(p) => (p.$selected ? TITLE_COLOR : '#374151')};
  background: ${(p) => (p.$selected ? 'rgba(79,114,164,0.08)' : 'transparent')};
  border: none;
  cursor: pointer;

  &:hover {
    background: ${(p) => (p.$selected ? 'rgba(79,114,164,0.12)' : '#f3f4f6')};
  }
`

// ── Chart mode toggle (Share / Count segmented buttons) ──────────────────────

export const ChartModeButtonGroup = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${CARD_BORDER};
  border-radius: 8px;
  overflow: hidden;
`

export const ChartModeButton = styled.button<{
  $active?: boolean
  $first?: boolean
}>`
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$active ? '700' : '500')};
  color: ${(p) => (p.$active ? '#28364F' : SECTION_LABEL_COLOR)};
  background: #ffffff;
  border: none;
  border-left: ${(p) => (p.$first ? 'none' : `1px solid ${CARD_BORDER}`)};
  cursor: pointer;
  line-height: 1.4;
  white-space: nowrap;

  &:hover {
    background: #f9fafb;
  }
`

// ── View all link ─────────────────────────────────────────────────────────────

export const ViewAllLink = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  text-decoration: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
    color: ${TREND_DETECTION_PRIMARY_BLUE};
  }
`
